import { Link, useNavigate } from "react-router-dom"
import { useTheme } from "@/context/ThemeContext"
import { Search, Moon, Sun, Menu, PenSquare, Bell, User, Settings, LogOut, Bookmark, FileText, Briefcase } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { useAuth } from "@/context/AuthContext"
import { logOut } from "@/lib/auth"
import { getInitials } from "@/lib/utils"
import { getUnreadNotificationCount } from "@/lib/db"
import { supabase } from "@/lib/supabase"
import { useState, useEffect } from "react"

export function Header() {
  const { theme, setTheme } = useTheme()
  const { user, firebaseUser, loading } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  // Load unread notification count
  useEffect(() => {
    if (!firebaseUser) {
      setUnreadCount(0)
      return
    }

    async function loadUnreadCount() {
      try {
        const count = await getUnreadNotificationCount(firebaseUser!.id)
        setUnreadCount(count)
      } catch (error) {
        console.error("Failed to load unread count:", error)
      }
    }

    loadUnreadCount()

    // Subscribe to real-time notification updates
    const channel = supabase
      .channel("notifications")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${firebaseUser.id}` }, () => {
        loadUnreadCount()
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [firebaseUser])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }
  }

  const handleLogout = async () => {
    await logOut()
    navigate("/")
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <svg viewBox="0 0 32 32" className="w-6 h-6">
              <g transform="translate(16, 16)">
                <ellipse cx="0" cy="0" rx="9" ry="3.6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.85" transform="rotate(-30)" />
                <ellipse cx="0" cy="0" rx="9" ry="3.6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.85" transform="rotate(30)" />
                <ellipse cx="0" cy="0" rx="9" ry="3.6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.85" transform="rotate(90)" />
                <circle cx="0" cy="0" r="1.8" fill="currentColor" />
              </g>
            </svg>
          </div>
          <span className="hidden font-bold text-foreground sm:inline-block">React Native News</span>
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center space-x-1">
          <Link to="/explore">
            <Button variant="ghost" size="sm">Explore</Button>
          </Link>
          <Link to="/jobs">
            <Button variant="ghost" size="sm">
              <Briefcase className="mr-1.5 h-4 w-4" />
              Jobs
            </Button>
          </Link>
        </nav>

        {/* Search bar - Desktop */}
        <form onSubmit={handleSearch} className="hidden flex-1 max-w-md mx-8 md:flex">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search posts, tags, authors..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10"
            />
          </div>
        </form>

        {/* Right side actions */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {!loading && (
            <>
              {firebaseUser ? (
                <>
                  {/* Write button */}
                  <Link to="/write">
                    <Button variant="ghost" size="sm" className="hidden sm:flex">
                      <PenSquare className="mr-2 h-4 w-4" />
                      Write
                    </Button>
                  </Link>

                  {/* Notifications */}
                  <Link to="/notifications">
                    <Button variant="ghost" size="icon" className="relative">
                      <Bell className="h-5 w-5" />
                      {unreadCount > 0 && (
                        <Badge className="absolute -right-1 -top-1 h-5 min-w-5 px-1 text-xs" variant="destructive">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </Link>

                  {/* User menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                        <Avatar className="h-8 w-8">
                          <AvatarImage
                            src={user?.avatarUrl || firebaseUser.user_metadata?.avatar_url || firebaseUser.user_metadata?.picture}
                            alt={user?.displayName || firebaseUser.user_metadata?.full_name || "User"}
                          />
                          <AvatarFallback>
                            {getInitials(user?.displayName || firebaseUser.user_metadata?.full_name || firebaseUser.email || "U")}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="end">
                      <DropdownMenuLabel>
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium">{user?.displayName || firebaseUser.user_metadata?.full_name || "User"}</p>
                          <p className="text-xs text-muted-foreground">{user ? `@${user.username}` : firebaseUser.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {user && (
                        <>
                          <DropdownMenuItem asChild>
                            <Link to={`/user/${user.username}`}>
                              <User className="mr-2 h-4 w-4" />
                              Profile
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/dashboard">
                              <PenSquare className="mr-2 h-4 w-4" />
                              Dashboard
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/drafts">
                              <FileText className="mr-2 h-4 w-4" />
                              Drafts
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/saved">
                              <Bookmark className="mr-2 h-4 w-4" />
                              Reading List
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/post-job">
                              <Briefcase className="mr-2 h-4 w-4" />
                              Post a Job
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/settings">
                              <Settings className="mr-2 h-4 w-4" />
                              Settings
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                            {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                            {theme === "dark" ? "Light mode" : "Dark mode"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      Log in
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button size="sm">Sign up</Button>
                  </Link>
                </>
              )}
            </>
          )}

          {/* Mobile menu button */}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-border p-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input type="search" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
          </form>
          <nav className="space-y-2">
            <Link to="/explore" className="block py-2 text-muted-foreground hover:text-foreground">
              Explore
            </Link>
            <Link to="/tags" className="block py-2 text-muted-foreground hover:text-foreground">
              Tags
            </Link>
            <Link to="/jobs" className="block py-2 text-muted-foreground hover:text-foreground">
              Jobs
            </Link>
            {firebaseUser && (
              <>
                <Link to="/write" className="block py-2 text-muted-foreground hover:text-foreground">
                  Write a Post
                </Link>
                <button onClick={handleLogout} className="block w-full text-left py-2 text-muted-foreground hover:text-foreground">
                  Log out
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  )
}
